import {
  Box,
  Button,
  Col,
  DatePickerField,
  FieldLabel,
  Flex,
  Form,
  Loading,
  MonetaryInputField,
  RemovableCard,
  Row,
  SelectField,
  SubmitButton,
  Text,
  TextAreaInputField,
} from '@/components';
import {
  useAccountTitles,
  useAccountingTypeOptions,
} from '@/features/master-table/hooks';
import { useProject } from '@/features/project/_hooks/project';
import { isBefore2025 } from '@/features/project/_utils/project';
import {
  ActionOfReflectionStatusLabelModifier,
  InspectionByExternalExpertReasonModifier,
  InspectionEvaluationFormSchema,
  OpinionByAdministrativeReviewTeamLabelModifier,
  ProcessTypeLabelModifier,
  actionOfReflectionStatus,
  inspectionByExternalExpertReason,
  opinionByAdministrativeReviewTeam,
  processType,
} from '@/features/project/projects/_models/project';
import { useForm, useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import {
  ReviewTypeLabelModifier,
  reviewTypeEnum,
  type sheetTypeEnum,
} from '@/utils/enum';
import { PlusOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { type FC, useMemo } from 'react';
import { FormProvider, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useRequiredByEndYearOrOutcomeTargetYear } from '../_hooks/form-control';

type FormValues = z.infer<typeof InspectionEvaluationFormSchema>;

type Props = {
  fiscalYear: number;
  type: z.infer<typeof sheetTypeEnum>;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const InspectionEvaluationForm: FC<Props> = ({
  fiscalYear,
  type,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id } = useId();
  const project = useProject(id);
  const accountingTypeOptions = useAccountingTypeOptions({ type: 'special' });
  const accountTitles = useAccountTitles();

  const { required: requiredEvaluation, isLoading } =
    useRequiredByEndYearOrOutcomeTargetYear(id);

  const method = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      InspectionEvaluationFormSchema.refine(
        (data) => {
          if (type === 'RS' || type === 'SS') {
            return !!data.inspectionAndImprovementByAdministrativeJurisdiction
              ?.inspectionResult;
          }
          return true;
        },
        {
          path: [
            'inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult',
          ],
          message: t('validation.required'),
        },
      )
        .refine(
          (data) => {
            if (type === 'RS' || type === 'SS') {
              return !!data.inspectionAndImprovementByAdministrativeJurisdiction
                ?.directionOfImprovement;
            }
            return true;
          },
          {
            path: [
              'inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement',
            ],
            message: t('validation.required'),
          },
        )
        .refine(
          (data) => {
            if (
              type === 'RS' &&
              'inspectionByExternalExpert' in data &&
              data.inspectionByExternalExpert?.reviewType === 'public-process'
            ) {
              return !!data.inspectionByExternalExpert?.resultOfPublishing;
            }
            return true;
          },
          {
            path: ['inspectionByExternalExpert.resultOfPublishing'],
            message: t('validation.required'),
          },
        )
        .refine(
          (data) => {
            if (
              type === 'RS' &&
              'inspectionByExternalExpert' in data &&
              data.inspectionByExternalExpert?.reviewType === 'document'
            ) {
              return !!data.inspectionByExternalExpert?.opinion;
            }
            return true;
          },
          {
            path: ['inspectionByExternalExpert.opinion'],
            message: t('validation.required'),
          },
        )
        .refine(
          (data) => {
            if (
              type === 'RS' &&
              'inspectionByExternalExpert' in data &&
              (data.inspectionByExternalExpert?.reviewType ===
                'public-process' ||
                data.inspectionByExternalExpert?.reviewType === 'document')
            ) {
              return !!data.inspectionByExternalExpert?.reason;
            }
            return true;
          },
          {
            path: ['inspectionByExternalExpert.reason'],
            message: t('validation.required'),
          },
        ),
    ),
  });

  const {
    handleSubmit,
    watch,
    setValue,
    resetField,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    setError,
  } = method;

  const {
    fields: summariesAndResponsesFields,
    append: appendSummariesAndResponses,
    remove: removeSummariesAndResponses,
  } = useFieldArray({
    control,
    name: 'summariesAndResponses',
  });

  const {
    fields: indicatedItemsAndResponsesFields,
    append: appendIndicatedItemsAndResponses,
    remove: removeIndicatedItemsAndResponses,
  } = useFieldArray({
    control,
    name: 'otherIndicatedItemsAndResponses',
  });

  const {
    fields: specialAccountFields,
    append: appendSpecialAccount,
    remove: removeSpecialAccount,
  } = useFieldArray({
    control,
    name: 'reflectionStatusBasedOnOpinion.specialAccount',
  });

  const [
    reflectionStatusBasedOnOpinionAction,
    inspectionByExternalExpertReviewType,
  ] = watch([
    'reflectionStatusBasedOnOpinion.action',
    'inspectionByExternalExpert.reviewType',
  ]);

  const disabledReflectionStatusBasedOnOpinionAmount = useMemo(
    () =>
      reflectionStatusBasedOnOpinionAction !== 'abolition' &&
      reflectionStatusBasedOnOpinionAction !== 'reduction',
    [reflectionStatusBasedOnOpinionAction],
  );

  return isLoading ? (
    <Loading />
  ) : (
    <FormProvider {...method}>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        layout="vertical"
        onFinish={handleSubmit((values) => {
          if (
            requiredEvaluation &&
            type !== 'KS' &&
            !values.inspectionAndImprovementByAdministrativeJurisdiction
              ?.evaluation
          ) {
            setError(
              'inspectionAndImprovementByAdministrativeJurisdiction.evaluation',
              {
                type: 'required',
                message: t('validation.required'),
              },
            );
            return;
          }

          onSubmit(values);
        })}
      >
        {type === 'RS' && (
          <>
            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t(
                  'inspection-and-improvement-by-administrative-jurisdiction',
                )}
              />
            </Box>
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult"
              label={t('inspection-result')}
              hint={t('hint-inspection-result-by-administrative-jurisdiction')}
              control={control}
              errors={errors}
              rows={2}
              required
            />
            <EvaluationField required={requiredEvaluation} type={type} />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement"
              label={t('direction-of-improvement')}
              hint={t(
                'hint-direction-of-improvement-by-administrative-jurisdiction',
              )}
              control={control}
              errors={errors}
              rows={2}
              required
            />

            <Box marginBottom={12}>
              <Text variant="h5" text={t('inspection-by-external-expert')} />
            </Box>
            <SelectField
              name="inspectionByExternalExpert.reviewType"
              label={t('inspection-target')}
              control={control}
              errors={errors}
              options={reviewTypeEnum.options.map((value) => ({
                value,
                label: ReviewTypeLabelModifier[value],
              }))}
              shouldCoexistOnChange
              onChange={(value) => {
                if (value === 'public-process') {
                  resetField('inspectionByExternalExpert.opinion', {
                    keepDirty: false,
                    keepTouched: false,
                    keepError: false,
                    defaultValue: null,
                  });
                } else if (value === 'unnecessary') {
                  resetField('inspectionByExternalExpert.reason', {
                    keepDirty: false,
                    keepTouched: false,
                    keepError: false,
                    defaultValue: null,
                  });
                }
              }}
            />
            <DatePickerField
              name="inspectionByExternalExpert.lastImplementedFiscalYear"
              label={t('last-implemented-fiscal-year')}
              hint={t('hint-last-implemented-fiscal-year-by-external-expert')}
              control={control}
              errors={errors}
              picker="year"
              disabledDate={(current) => {
                return current > dayjs().year(fiscalYear).endOf('year');
              }}
            />
            <SelectField
              name="inspectionByExternalExpert.reason"
              label={t('reason-of-target')}
              hint={t('hint-reason-of-target-by-external-expert')}
              control={control}
              errors={errors}
              options={inspectionByExternalExpertReason.options.map(
                (value) => ({
                  value,
                  label: InspectionByExternalExpertReasonModifier[value],
                }),
              )}
              disabled={inspectionByExternalExpertReviewType === 'unnecessary'}
              required={
                inspectionByExternalExpertReviewType === 'public-process' ||
                inspectionByExternalExpertReviewType === 'document'
              }
            />
            <TextAreaInputField
              name="inspectionByExternalExpert.opinion"
              label={t('opinion')}
              hint={t('hint-opinion-by-external-expert')}
              control={control}
              errors={errors}
              rows={2}
              disabled={
                inspectionByExternalExpertReviewType === 'public-process'
              }
              required={inspectionByExternalExpertReviewType === 'document'}
            />
            <TextAreaInputField
              name="inspectionByExternalExpert.resultOfPublishing"
              label={t('result-of-publishing')}
              hint={t('hint-result-of-publishing-by-external-expert')}
              control={control}
              errors={errors}
              rows={2}
              required={
                inspectionByExternalExpertReviewType === 'public-process'
              }
            />

            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t('process-and-opinion-by-administrative-review-team')}
              />
            </Box>
            <SelectField
              name="processAndOpinionByAdministrativeReviewTeam.opinion"
              label={t('opinion')}
              hint={t('hint-opinion-by-administrative-review-team')}
              control={control}
              errors={errors}
              options={opinionByAdministrativeReviewTeam.options.map(
                (value) => ({
                  value,
                  label: OpinionByAdministrativeReviewTeamLabelModifier[value],
                }),
              )}
            />
            <TextAreaInputField
              name="processAndOpinionByAdministrativeReviewTeam.context"
              label={t('details')}
              hint={t(
                'hint-details-of-process-and-opinion-by-administrative-review-team',
              )}
              control={control}
              errors={errors}
              rows={2}
            />

            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t('reflection-status-based-on-opinion')}
              />
            </Box>
            <SelectField
              name="reflectionStatusBasedOnOpinion.action"
              label={t('improvement-and-reflection-status')}
              hint={t('hint-improvement-and-reflection-status')}
              control={control}
              errors={errors}
              options={actionOfReflectionStatus.options.map((value) => ({
                value,
                label: ActionOfReflectionStatusLabelModifier[value],
              }))}
            />
            <TextAreaInputField
              name="reflectionStatusBasedOnOpinion.context"
              label={t('details')}
              hint={t('hint-details-of-reflection-status-based-on-opinion')}
              control={control}
              errors={errors}
              rows={2}
            />
            <MonetaryInputField
              name="reflectionStatusBasedOnOpinion.reflectedAmountOfGeneralAccount"
              control={control}
              label={t('reflected-amount-of-general-account')}
              errors={errors}
              unit={unit}
              disabled={disabledReflectionStatusBasedOnOpinionAmount}
              min={0}
            />
            <Form.Item label={t('reflected-amount-of-special-account')}>
              <Row gutter={[0, 8]}>
                {specialAccountFields.map((item, idx) => {
                  const accountingTypeId = watch(
                    `reflectionStatusBasedOnOpinion.specialAccount.${idx}.accountingTypeId`,
                  );
                  const accountTitleId = watch(
                    `reflectionStatusBasedOnOpinion.specialAccount.${idx}.accountTitleId`,
                  );
                  return (
                    <Col key={item.id} span={24}>
                      <RemovableCard
                        onRemove={() => {
                          removeSpecialAccount(idx);
                        }}
                      >
                        <Row gutter={[8, 8]} wrap>
                          <Col span={12}>
                            <SelectField
                              name={`reflectionStatusBasedOnOpinion.specialAccount.${idx}.accountingTypeId`}
                              label={t('accounting-name')}
                              control={control}
                              errors={errors}
                              options={accountingTypeOptions.data?.filter(
                                (accountingTypeOption) => {
                                  if (
                                    accountingTypeOption.value ===
                                    accountingTypeId
                                  ) {
                                    return true;
                                  }
                                  return project.data?.subProjects
                                    .flatMap((subProject) =>
                                      subProject.expenditureCarts.flatMap(
                                        (expenditureCart) =>
                                          expenditureCart.accountingId
                                            ? expenditureCart.accountingId
                                            : [],
                                      ),
                                    )
                                    .includes(accountingTypeOption.value);
                                },
                              )}
                              disabled={
                                disabledReflectionStatusBasedOnOpinionAmount
                              }
                              onChange={(value) => {
                                setValue(
                                  `reflectionStatusBasedOnOpinion.specialAccount.${idx}.accountingTypeId`,
                                  value,
                                );
                                resetField(
                                  `reflectionStatusBasedOnOpinion.specialAccount.${idx}.accountTitleId`,
                                  { defaultValue: null },
                                );
                              }}
                            />
                          </Col>
                          <Col span={12}>
                            <SelectField
                              name={`reflectionStatusBasedOnOpinion.specialAccount.${idx}.accountTitleId`}
                              label={t('account-title')}
                              control={control}
                              errors={errors}
                              options={accountTitles.data?.flatMap(
                                (accountTitle) => {
                                  if (accountTitle.id === accountTitleId) {
                                    return {
                                      value: accountTitle.id,
                                      label: accountTitle.name,
                                    };
                                  }
                                  if (
                                    accountTitle.accountingId !==
                                    accountingTypeId
                                  ) {
                                    return [];
                                  }
                                  return project.data?.subProjects
                                    .flatMap((subProject) =>
                                      subProject.expenditureCarts.flatMap(
                                        (expenditureCart) =>
                                          expenditureCart.accountTitleId
                                            ? expenditureCart.accountTitleId
                                            : [],
                                      ),
                                    )
                                    .includes(accountTitle.id)
                                    ? {
                                        value: accountTitle.id,
                                        label: accountTitle.name,
                                      }
                                    : [];
                                },
                              )}
                              disabled={
                                !accountingTypeId ||
                                disabledReflectionStatusBasedOnOpinionAmount
                              }
                            />
                          </Col>
                          <Col span={12}>
                            <MonetaryInputField
                              name={`reflectionStatusBasedOnOpinion.specialAccount.${idx}.reflectedAmount`}
                              control={control}
                              label={t('reflected-amount')}
                              errors={errors}
                              unit={unit}
                              disabled={
                                disabledReflectionStatusBasedOnOpinionAmount
                              }
                              min={0}
                            />
                          </Col>
                        </Row>
                      </RemovableCard>
                    </Col>
                  );
                })}
                <Button
                  type="dashed"
                  onClick={() => {
                    appendSpecialAccount({
                      accountingTypeId: null,
                      accountTitleId: null,
                      reflectedAmount: null,
                    });
                  }}
                  block
                  disabled={disabledReflectionStatusBasedOnOpinionAmount}
                  icon={<PlusOutlined />}
                >
                  {t('add-reflected-amount-of-special-account')}
                </Button>
              </Row>
            </Form.Item>

            <Box marginBottom={12}>
              <Text variant="h5" text={t('indicated-item-and-response')} />
            </Box>
            <Form.Item
              label={
                <FieldLabel
                  text={t('summary-of-publishing')}
                  hint={t('hint-summary-of-publishing')}
                />
              }
            >
              <Flex vertical gap={8}>
                {summariesAndResponsesFields.map((item, idx) => {
                  return (
                    <RemovableCard
                      key={item.id}
                      onRemove={() => {
                        removeSummariesAndResponses(idx);
                      }}
                    >
                      <Row gutter={[8, 8]} wrap justify="space-between">
                        <Col span={8}>
                          <SelectField
                            name={`summariesAndResponses.${idx}.process`}
                            label={t('summary-process')}
                            control={control}
                            errors={errors}
                            options={processType.options.map((value) => ({
                              value,
                              label: ProcessTypeLabelModifier[value],
                            }))}
                          />
                        </Col>
                        <Col span={12}>
                          <DatePickerField
                            name={`summariesAndResponses.${idx}.fiscalYear`}
                            label={t('summary-fiscal-year')}
                            control={control}
                            errors={errors}
                            picker="year"
                          />
                        </Col>
                        <Col span={24}>
                          <TextAreaInputField
                            name={`summariesAndResponses.${idx}.content`}
                            label={t('summary-content')}
                            control={control}
                            errors={errors}
                            rows={2}
                          />
                        </Col>
                        <Col span={24}>
                          <TextAreaInputField
                            name={`summariesAndResponses.${idx}.response`}
                            label={t('response-status')}
                            control={control}
                            errors={errors}
                            rows={2}
                          />
                        </Col>
                      </Row>
                    </RemovableCard>
                  );
                })}
                <Button
                  type="dashed"
                  onClick={() => {
                    appendSummariesAndResponses({
                      process: null,
                      fiscalYear: null,
                      content: null,
                      response: null,
                    });
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  {t('add-summary')}
                </Button>
              </Flex>
            </Form.Item>

            <Form.Item
              label={
                <FieldLabel
                  text={t('others-indicated-item')}
                  hint={t('hint-others-indicated-item-for-rs')}
                />
              }
            >
              <Flex vertical gap={8}>
                {indicatedItemsAndResponsesFields.map((item, idx) => {
                  return (
                    <RemovableCard
                      key={item.id}
                      onRemove={() => {
                        removeIndicatedItemsAndResponses(idx);
                      }}
                    >
                      <Flex vertical gap={8}>
                        <TextAreaInputField
                          name={`otherIndicatedItemsAndResponses.${idx}.reason`}
                          label={t('indicated-reason')}
                          control={control}
                          errors={errors}
                        />
                        <DatePickerField
                          name={`otherIndicatedItemsAndResponses.${idx}.fiscalYear`}
                          label={t('indicated-fiscal-year')}
                          control={control}
                          errors={errors}
                          picker="year"
                        />
                        <TextAreaInputField
                          name={`otherIndicatedItemsAndResponses.${idx}.content`}
                          label={t('indicated-content')}
                          control={control}
                          errors={errors}
                          rows={2}
                        />
                        <TextAreaInputField
                          name={`otherIndicatedItemsAndResponses.${idx}.response`}
                          label={t('response-status')}
                          control={control}
                          errors={errors}
                          rows={2}
                        />
                      </Flex>
                    </RemovableCard>
                  );
                })}
                <Button
                  type="dashed"
                  onClick={() => {
                    appendIndicatedItemsAndResponses({
                      reason: null,
                      fiscalYear: null,
                      content: null,
                      response: null,
                    });
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  {t('add-others-indicated-item')}
                </Button>
              </Flex>
            </Form.Item>
          </>
        )}

        {type === 'SS' && (
          <>
            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t(
                  'inspection-and-improvement-by-iaa-administrative-jurisdiction',
                )}
              />
            </Box>
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult"
              label={t('inspection-result')}
              hint={t(
                'hint-inspection-result-by-iaa-administrative-jurisdiction',
              )}
              control={control}
              errors={errors}
              required
            />
            <EvaluationField required={requiredEvaluation} type={type} />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement"
              label={t('direction-of-improvement')}
              hint={t(
                'hint-direction-of-improvement-by-iaa-administrative-jurisdiction',
              )}
              control={control}
              errors={errors}
              required
            />
          </>
        )}

        {type === 'KS' && (
          <>
            {isBefore2025(project.data) && (
              <>
                <Box marginBottom={12}>
                  <Text variant="h5" text={t('appropriateness-inspection')} />
                </Box>
                <TextAreaInputField
                  name="appropriatenessInspection.reasonsWhyBulkDeliveringWasNecessary"
                  label={t('reason-why-bulk-delivering-was-necessary')}
                  hint={t('hint-reason-why-bulk-delivering-was-necessary')}
                  control={control}
                  errors={errors}
                  rows={2}
                />
                <TextAreaInputField
                  name="appropriatenessInspection.approachInDeterminingTheTimingAndAmountOfAdditions"
                  label={t(
                    'approach-in-determining-the-timing-and-amount-of-additions',
                  )}
                  hint={t(
                    'hint-approach-in-determining-the-timing-and-amount-of-additions',
                  )}
                  control={control}
                  errors={errors}
                  rows={2}
                />
              </>
            )}
            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t('inspection-and-improvement-by-fund-jurisdiction')}
              />
            </Box>
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult"
              label={t('inspection-result')}
              control={control}
              errors={errors}
              rows={2}
            />
            <EvaluationField required={false} type={type} />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement"
              label={t('direction-of-improvement')}
              control={control}
              errors={errors}
              rows={2}
            />

            <Box marginBottom={12}>
              <Text variant="h5" text={t('findings-of-external-experts')} />
            </Box>
            <TextAreaInputField
              name="findingsOfExternalExperts"
              label=""
              control={control}
              errors={errors}
              rows={2}
            />

            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t(
                  'process-leading-to-the-findings-of-review-promotion-team',
                )}
              />
            </Box>
            <TextAreaInputField
              name="processLeadingToTheFindingsOfReviewPromotionTeam"
              label=""
              control={control}
              errors={errors}
              rows={2}
            />

            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t('improvement-point-based-on-the-findings')}
              />
            </Box>
            <TextAreaInputField
              name="improvementPointBasedOnTheFindings"
              label=""
              control={control}
              errors={errors}
              rows={2}
            />

            {isBefore2025(project.data) && (
              <>
                <Box marginBottom={12}>
                  <Text
                    variant="h5"
                    text={t('overview-of-reviews-carried-out-in-the-past')}
                  />
                </Box>
                <TextAreaInputField
                  name="overviewOfReviewsCarriedOutInThePast"
                  label=""
                  control={control}
                  errors={errors}
                  rows={2}
                />
              </>
            )}
          </>
        )}

        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      </Form>
    </FormProvider>
  );
};

const EvaluationField: FC<{
  required: boolean;
  type: z.infer<typeof sheetTypeEnum>;
}> = ({ required, type }) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  const hint =
    type === 'RS'
      ? t('hint-evaluation-by-administrative-jurisdiction')
      : type === 'SS'
        ? t('hint-evaluation-by-iaa-administrative-jurisdiction')
        : undefined;

  return (
    <TextAreaInputField
      name="inspectionAndImprovementByAdministrativeJurisdiction.evaluation"
      label={t('evaluation')}
      hint={hint}
      control={control}
      errors={errors}
      required={required}
      rows={2}
    />
  );
};
